import { createTheme, colors } from '@mui/material';
import { get } from 'lodash-es';
import palette from './palette';
import typography from './typography';
// eslint-disable-next-line @typescript-eslint/default-param-last
const fontProperties = (font, defaults = {}, replaceColor) => ({
    fontSize: font?.fontSize ? `${font?.fontSize || ''}px` : defaults.fontSize,
    color: replaceColor(font?.color),
    fontWeight: font?.fontWeight || defaults.fontWeight,
    textTransform: font?.textTransform,
});
const buttonProperties = (button, replaceColor) => ({
    color: replaceColor(button?.font?.color),
    fontWeight: button?.font?.fontWeight,
    fontSize: `${button?.font?.fontSize}px`,
    backgroundColor: replaceColor(button?.background?.color),
    borderColor: replaceColor(button?.border?.color),
    textTransform: button?.font?.textTransform,
    border: `${button?.border?.thickness || ''}px solid`,
    borderRadius: typeof button?.border?.radius === 'number' ? `${button.border.radius}px` : undefined,
    '&:hover': {
        color: replaceColor(button?.font?.hover?.color),
        backgroundColor: replaceColor(button?.background?.hover?.color),
        borderColor: replaceColor(button?.border?.hover?.color),
        textTransform: button?.font?.textTransform,
        border: `${button?.border?.thickness || ''}px solid`,
        borderRadius: typeof button?.border?.radius === 'number' ? `${button.border.radius}px` : undefined,
    },
    '&:disabled': {
        color: replaceColor(button?.font?.disabled?.color),
        backgroundColor: replaceColor(button?.background?.disabled?.color),
        borderColor: replaceColor(button?.border?.disabled?.color),
        textTransform: button?.font?.textTransform,
        border: `${button?.border?.thickness || ''}px solid`,
        borderRadius: typeof button?.border?.radius === 'number' ? `${button.border.radius}px` : undefined,
    },
});
const chipProperties = (chip, replaceColor) => ({
    ...buttonProperties(chip, replaceColor),
    '&.MuiChip-filled': {
        backgroundColor: replaceColor(chip?.selectedColor) || replaceColor(chip?.background?.color),
    },
    '&.MuiChip-outlined': {
        backgroundColor: replaceColor(chip?.background?.color),
        color: replaceColor(chip?.font?.color),
        textTransform: chip?.font?.textTransform,
        border: `${chip?.border?.thickness || ''}px solid ${replaceColor(chip?.border?.color) ?? ''}`,
        borderRadius: typeof chip?.border?.radius === 'number' ? `${chip.border.radius}px` : undefined,
    },
    '&&:hover': {
        color: replaceColor(chip?.font?.hover?.color),
        backgroundColor: replaceColor(chip?.background?.hover?.color),
        textTransform: chip?.font?.textTransform,
        border: `${chip?.border?.thickness || ''}px solid ${replaceColor(chip?.border?.hover?.color) ?? ''}`,
        borderRadius: typeof chip?.border?.radius === 'number' ? `${chip.border.radius}px` : undefined,
    },
});
const tabProperties = (tab, replaceColor) => ({
    color: replaceColor(tab?.font?.color),
    fontWeight: tab?.font?.fontWeight,
    fontSize: `${tab?.font?.fontSize || ''}px`,
    textTransform: tab?.font?.textTransform,
    backgroundColor: replaceColor(tab?.background?.color),
    border: `${tab?.default?.border?.thickness || ''}px solid`,
    borderColor: replaceColor(tab?.border?.color),
    '&:hover': {
        color: replaceColor(tab?.font?.hover?.color),
        backgroundColor: replaceColor(tab?.background?.hover?.color),
        borderColor: tab?.border?.hover?.color,
    },
    '&.Mui-selected': {
        borderBottom: 1,
        color: replaceColor(tab?.font?.hover?.color),
        backgroundColor: replaceColor(tab?.background?.hover?.color),
        borderColor: replaceColor(tab?.border?.hover?.color),
    },
});
const colorMap = {
    color_font: 'font.color',
    color_font_secondary: 'secondaryFont.color',
    color_font_inverse: 'inverseFont.color',
    color_primary: 'primary.color',
    color_secondary: 'secondary.color',
    color_tertiary: 'tertiary.color',
    color_primary_hover: 'primary.hover.color',
    color_secondary_hover: 'secondary.hover.color',
    color_tertiary_hover: 'tertiary.hover.color',
    color_primary_disabled: 'primary.disabled.color',
    color_secondary_disabled: 'secondary.disabled.color',
    color_tertiary_disabled: 'tertiary.disabled.color',
    color_background: 'background.color',
};
export const globalDefaults = {
    primary: {
        color: '#007bad',
        hover: { color: '#005c8c' },
        disabled: { color: '#091E424F' },
    },
    secondary: {
        color: '#e26c3a',
        hover: { color: '#d45013' },
        disabled: { color: '#091E424F' },
    },
    tertiary: {
        color: '#0B5197',
        hover: { color: '#1564C0' },
        disabled: { color: '#091E424F' },
    },
    background: {
        color: '#ffffff',
    },
    font: {
        color: '#4a4a4a',
    },
    inverseFont: {
        color: '#f3f3f3',
    },
    secondaryFont: {
        color: '#e26c3a',
    },
};
export const replaceStylesheetGlobals = (styleWithTokens, text) => {
    if (!text?.replace) {
        return '';
    }
    // do the longer matches first or color_primary also matches color_primary_hover
    return text
        .replace(/color_primary_hover/g, styleWithTokens.global?.primary?.hover?.color || globalDefaults.primary.hover.color)
        .replace(/color_secondary_hover/g, styleWithTokens.global?.secondary?.hover?.color || globalDefaults.secondary.hover.color)
        .replace(/color_tertiary_hover/g, styleWithTokens.global?.tertiary?.hover?.color || globalDefaults.tertiary.hover.color)
        .replace(/color_primary_disabled/g, styleWithTokens.global?.primary?.disabled?.color || globalDefaults.primary.disabled.color)
        .replace(/color_secondary_disabled/g, styleWithTokens.global?.secondary?.disabled?.color || globalDefaults.secondary.disabled.color)
        .replace(/color_tertiary_disabled/g, styleWithTokens.global?.tertiary?.disabled?.color || globalDefaults.tertiary.disabled.color)
        .replace(/color_background/g, styleWithTokens.global?.background?.color || globalDefaults.background.color)
        .replace(/color_primary/g, styleWithTokens.global?.primary?.color || globalDefaults.primary.color)
        .replace(/color_secondary/g, styleWithTokens.global?.secondary?.color || globalDefaults.secondary.color)
        .replace(/color_tertiary/g, styleWithTokens.global?.tertiary?.color || globalDefaults.tertiary.color)
        .replace(/color_font_inverse/g, styleWithTokens.global?.inverseFont?.color || globalDefaults.inverseFont.color)
        .replace(/color_font_secondary/g, styleWithTokens.global?.secondaryFont?.color || globalDefaults.secondaryFont.color)
        .replace(/color_font/g, styleWithTokens.global?.font?.color || globalDefaults.font.color);
};
export const stylesheetDefault = {
    global: globalDefaults,
    site: {
        buttons: {
            border: {
                hover: {
                    color: 'color_primary_hover',
                },
                color: 'color_primary',
                disabled: {
                    color: 'color_primary_disabled',
                },
                radius: 22,
                thickness: 2,
            },
            background: {
                hover: {
                    color: 'color_background',
                },
                color: 'color_background',
                disabled: {
                    color: 'color_background',
                },
            },
            font: {
                hover: {
                    color: 'color_primary_hover',
                },
                color: 'color_primary',
                disabled: {
                    color: 'color_primary_disabled',
                },
                textTransform: 'none',
                fontSize: 14,
                fontWeight: 'normal',
            },
        },
        searchBar: {
            border: {
                hover: {
                    color: 'color_primary_hover',
                },
                color: 'color_primary',
                disabled: {
                    color: 'color_primary_disabled',
                },
                radius: 22,
                thickness: 2,
            },
            background: {
                hover: {
                    color: 'color_background',
                },
                color: 'color_background',
                disabled: {
                    color: 'color_background',
                },
            },
            font: {
                hover: {
                    color: 'color_primary_hover',
                },
                color: 'color_primary',
                disabled: {
                    color: 'color_primary_disabled',
                },
                textTransform: 'none',
                fontSize: 12,
                fontWeight: 'normal',
            },
        },
        progressBar: {
            color: 'color_secondary',
            bar: {
                color: 'color_secondary_disabled',
            },
        },
        system: {
            buttons: {
                secondary: {
                    border: {
                        hover: {
                            color: 'color_secondary_hover',
                        },
                        color: 'color_secondary',
                        thickness: 1,
                        disabled: {
                            color: 'color_secondary_disabled',
                        },
                        radius: 6,
                    },
                    background: {
                        hover: {
                            color: 'rgba(0,0,0,0)',
                        },
                        color: 'rgba(0,0,0,0)',
                        disabled: {
                            color: 'rgba(0,0,0,0)',
                        },
                    },
                    font: {
                        hover: {
                            color: 'color_secondary_hover',
                        },
                        color: 'color_secondary',
                        disabled: {
                            color: 'color_secondary_disabled',
                        },
                        fontSize: 14,
                        fontWeight: '400',
                        textTransform: 'none',
                    },
                },
                primary: {
                    border: {
                        hover: {
                            color: 'color_primary_hover',
                        },
                        color: 'color_primary',
                        thickness: 1,
                        disabled: {
                            color: 'color_primary_disabled',
                        },
                        radius: 6,
                    },
                    background: {
                        hover: {
                            color: 'color_primary_hover',
                        },
                        color: 'color_primary',
                        disabled: {
                            color: 'color_primary_disabled',
                        },
                    },
                    font: {
                        hover: {
                            color: 'color_font_inverse',
                        },
                        color: 'color_font_inverse',
                        disabled: {
                            color: 'color_font',
                        },
                        fontSize: 14,
                        fontWeight: '400',
                        textTransform: 'none',
                    },
                },
            },
            chips: {
                primary: {
                    border: {
                        hover: {
                            color: 'color_primary_hover',
                        },
                        color: 'color_primary',
                        thickness: 1,
                        disabled: {
                            color: 'color_primary_disabled',
                        },
                        radius: 16,
                    },
                    background: {
                        hover: {
                            color: 'color_primary_hover',
                        },
                        color: 'color_primary',
                        disabled: {
                            color: 'color_primary_disabled',
                        },
                    },
                    font: {
                        hover: {
                            color: 'color_font_inverse',
                        },
                        color: 'color_font_inverse',
                        disabled: {
                            color: 'color_font',
                        },
                        fontSize: 14,
                        fontWeight: '400',
                        textTransform: 'none',
                    },
                    selectedColor: 'color_primary_hover',
                },
                secondary: {
                    border: {
                        hover: {
                            color: 'color_secondary_hover',
                        },
                        color: 'color_secondary',
                        thickness: 1,
                        disabled: {
                            color: 'color_secondary_disabled',
                        },
                        radius: 16,
                    },
                    background: {
                        hover: {
                            color: 'color_secondary_hover',
                        },
                        color: 'color_secondary',
                        disabled: {
                            color: 'color_secondary_disabled',
                        },
                    },
                    font: {
                        hover: {
                            color: 'color_font_inverse',
                        },
                        color: 'color_font_inverse',
                        disabled: {
                            color: 'color_font',
                        },
                        fontSize: 14,
                        fontWeight: '400',
                        textTransform: 'none',
                    },
                    selectedColor: 'color_secondary_hover',
                },
            },
            pages: {
                pageTitle: {
                    font: {
                        color: 'color_font',
                        fontSize: 18,
                        fontWeight: '800',
                        textTransform: 'none',
                    },
                },
                message: {
                    font: {
                        color: 'color_font',
                        fontSize: 14,
                        fontWeight: 'normal',
                        textTransform: 'none',
                    },
                },
            },
        },
        background: {
            color: 'color_background',
        },
        paper: {
            color: 'color_background',
        },
        ctaButtons: {
            border: {
                color: 'color_secondary',
                hover: {
                    color: 'color_secondary_hover',
                },
                disabled: {
                    color: 'color_secondary_disabled',
                },
                radius: 6,
                thickness: 1,
            },
            font: {
                color: 'color_font_inverse',
                hover: {
                    color: 'color_font_inverse',
                },
                disabled: {
                    color: 'color_font_inverse',
                },
                textTransform: 'uppercase',
                fontSize: 14,
                fontWeight: '500',
            },
            background: {
                color: 'color_secondary',
                hover: {
                    color: 'color_secondary_hover',
                },
                disabled: {
                    color: 'color_secondary_disabled',
                },
            },
        },
        iconColor: {
            hover: {
                color: 'color_secondary_hover',
            },
            color: 'color_secondary',
        },
        tabs: {
            underline: {
                color: 'color_secondary',
            },
            font: {
                color: 'color_secondary',
                hover: {
                    color: 'color_secondary_hover',
                },
                disabled: {
                    color: 'color_secondary_disabled',
                },
                textTransform: 'none',
            },
            background: {
                color: 'rgba(0,0,0,0)',
                hover: {
                    color: 'rgba(0,0,0,0)',
                },
                disabled: {
                    color: 'rgba(0,0,0,0)',
                },
            },
        },
        lineColor: 'color_secondary',
        sidebar: {
            selectedItem: {
                backgroundColor: '#0a1d4a',
            },
            gradient: {
                topColor: '#142261',
                bottomColor: '#004789',
            },
        },
    },
    footer: {
        socialLinks: {
            color: 'color_tertiary',
        },
        heading: {
            font: {
                color: 'color_primary',
                fontSize: 16,
                fontWeight: '700',
                textTransform: 'none',
            },
        },
        background: {
            color: 'color_background',
        },
        link: {
            font: {
                color: 'color_tertiary',
                fontSize: 12,
                fontWeight: '400',
                textTransform: 'none',
            },
        },
    },
    menu: {
        menu: {
            font: {
                color: 'color_primary',
            },
        },
        subMenu: {
            font: {
                color: 'color_primary',
            },
        },
        menuSeparator: {
            color: 'color_tertiary',
        },
        subMenuSeparator: {
            color: 'color_tertiary',
        },
    },
    courseDisplay: {
        courseHeadline: {
            font: {
                color: 'color_font',
            },
        },
        instructor: {
            font: {
                color: 'color_primary',
            },
        },
        showMoreLinkColor: 'color_primary',
        courseTitleLandingPage: {
            font: {
                color: 'color_font',
            },
        },
        courseTitleFeatureCard: {
            font: {
                color: 'color_font',
            },
        },
        courseTitleSalesCard: {
            font: {
                color: 'color_font',
                textTransform: 'capitalize',
            },
        },
        description: {
            font: {
                color: 'color_font',
            },
        },
        sectionHeadings: {
            font: {
                color: 'color_font',
            },
        },
        courseInfoBarText: {
            font: {
                color: 'color_primary',
            },
        },
    },
    landingPage: {
        sectionHeaders: {
            font: {
                color: 'color_font',
                fontSize: 24,
                fontWeight: '700',
            },
        },
        courseInfoTabs: {
            underline: {
                color: 'color_primary',
            },
            background: {
                hover: {
                    color: 'rgba(0,0,0,0)',
                },
                color: 'rgba(0,0,0,0)',
                disabled: {
                    color: 'rgba(0,0,0,0)',
                },
            },
            font: {
                hover: {
                    color: 'color_primary_hover',
                },
                color: 'color_primary',
                disabled: {
                    color: 'color_primary_disabled',
                },
                fontWeight: '700',
                fontSize: 12,
                textTransform: 'capitalize',
            },
        },
        lineColor: 'color_primary_disabled',
        sectionSubHeaders: {
            font: {
                color: 'color_font',
                fontSize: 20,
                fontWeight: '700',
            },
        },
        sectionText: {
            font: {
                color: 'color_font',
                fontSize: 16,
                fontWeight: 'normal',
            },
        },
        bullet: {
            color: 'color_secondary',
        },
        tabs: {
            background: {
                color: '#f5f5f5',
            },
        },
    },
    courseDeliveryPage: {
        courseOutlineButton: {
            color: 'color_primary',
        },
        courseInfoBar: {
            background: {
                color: 'color_primary',
            },
        },
        tabText: {
            font: {
                color: 'color_font',
                fontSize: 13,
                fontWeight: '400',
            },
        },
        courseOutlineIcon: {
            color: 'color_font_inverse',
        },
        ctaLinksButton: {
            border: {
                color: 'color_secondary',
                hover: {
                    color: 'color_secondary_hover',
                },
                disabled: {
                    color: 'color_secondary_disabled',
                },
                radius: 6,
                thickness: 1,
            },
            font: {
                color: 'color_font_inverse',
                hover: {
                    color: 'color_font_inverse',
                },
                disabled: {
                    color: 'color_font_inverse',
                },
                textTransform: 'uppercase',
                fontSize: 14,
                fontWeight: '500',
            },
            background: {
                color: 'color_secondary',
                hover: {
                    color: 'color_secondary_hover',
                },
                disabled: {
                    color: 'color_secondary_disabled',
                },
            },
        },
        notesBoxIconColor: 'color_secondary',
        courseOutline: {
            font: {
                color: 'color_font_inverse',
            },
        },
        courseTabs: {
            underline: {
                color: 'rgba(0,0,0,0)',
            },
            background: {
                hover: {
                    color: 'color_background',
                },
                color: 'rgba(0,0,0,0)',
                disabled: {
                    color: 'rgba(0,0,0,0)',
                },
            },
            font: {
                hover: {
                    color: 'color_primary_hover',
                },
                color: 'color_primary',
                disabled: {
                    color: 'color_primary_disabled',
                },
                textTransform: 'capitalize',
                fontSize: 14,
                fontWeight: '700',
            },
        },
        callToActionButton: {
            border: {},
            font: {},
        },
        tabHeader: {
            font: {
                color: 'color_font',
                fontWeight: '700',
                fontSize: 16,
            },
        },
        lessonTitle: {
            font: {
                color: 'color_font',
            },
        },
        notesBoxTextColor: 'color_font',
        courseProgressBarColor: 'color_tertiary',
        courseProgressIconColor: 'color_secondary',
        courseProgressTextColor: 'color_font_inverse',
        courseBarLabels: {
            font: {
                color: 'color_font_inverse',
                textTransform: 'none',
                fontSize: 16,
                fontWeight: '600',
            },
        },
        courseBarTitle: {
            font: {
                color: 'color_font_inverse',
                fontSize: 16,
                fontWeight: '700',
            },
        },
    },
};
const colorMapLookup = (globals, colorCode) => (get(globals, colorMap[colorCode]) || colorCode);
const portalTheme = (stylesheetJSON) => {
    const style = (typeof stylesheetJSON !== 'object'
        || JSON.stringify(stylesheetJSON) === '{}')
        ? stylesheetDefault : stylesheetJSON;
    const globals = style.global;
    const replaceColor = (colorCode) => colorMapLookup(globals, colorCode);
    // console.log('globals', portal.stylesheet.global);
    const finalPalette = {
        palette: {
            ...palette,
            secondary: {
                main: '#e26c3a',
            },
            text: {
                primary: replaceColor(style?.global?.font?.color) || '#000000',
                secondary: '#ffffff',
            },
            background: {
                default: replaceColor(style?.site?.background?.color) || colors.common.white,
                paper: replaceColor(style?.site?.paper?.color) || colors.common.white,
            },
            info: {
                main: '#9ce2ff',
                light: '#acd3e3',
                dark: '#0d6bc2',
            },
            success: {
                main: '#a3cc06',
                //   light: '',
                dark: '#25a408',
            },
            divider: '#e4e7ed',
            subNav: {
                main: replaceColor(style.site?.subNav?.topColor),
                topColor: replaceColor(style.site?.sidebar?.gradient?.topColor)
                    || palette.indigoCustom[700],
                bottomColor: replaceColor(style.site?.sidebar?.gradient?.bottomColor)
                    || palette.indigoCustom[900],
                text: {
                    primary: '#ffffff',
                    secondary: '#acd3e3',
                },
            },
            courseBar: {
                main: '#007bad',
                text: {
                    primary: '#ffffff',
                    secondary: '#acd3e3',
                },
            },
            // ...style,
        },
        shape: { borderRadius: 5 },
        typography: {
            ...typography,
            fontFamily: style.site?.defaultFont || 'Roboto, Helvetica, Arial, sans-serif',
        },
        portal: {
            global: {
                primary: replaceColor(style.global?.primary),
                secondary: replaceColor(style.global?.secondary),
                font: {
                    color: replaceColor(style.global?.font?.color),
                },
                inverseFont: {
                    color: replaceColor(style.global?.inverseFont?.color),
                },
            },
            site: {
                background: {
                    color: replaceColor(style.site?.background?.color),
                },
                paper: {
                    color: replaceColor(style.site?.paper?.color),
                },
                searchBar: buttonProperties(style.site?.searchBar, replaceColor),
                lineColor: replaceColor(style.site?.lineColor),
                sidebar: {
                    selectedItem: {
                        backgroundColor: replaceColor(style.site?.sidebar?.selectedItem?.backgroundColor),
                    },
                    gradient: {
                        topColor: replaceColor(style.site?.paper?.color),
                        bottomColor: replaceColor(style.site?.paper?.color),
                    },
                },
                progressBar: {
                    color: replaceColor(style.site?.progressBar?.color),
                    bar: {
                        color: replaceColor(style.site?.progressBar?.bar?.color),
                    },
                },
            },
            footer: {
                background: {
                    color: replaceColor(style.footer?.background?.color),
                },
                links: {
                    color: replaceColor(style.footer?.links?.color),
                },
                socialLinks: {
                    color: replaceColor(style.footer?.socialLinks?.color),
                },
            },
            landingPage: {
                sectionText: {
                    font: {
                        color: replaceColor(style.landingPage?.sectionText?.font?.color),
                    },
                },
                sectionHeaders: {
                    font: {
                        color: replaceColor(style.landingPage?.sectionHeaders?.font?.color),
                    },
                },
                sectionSubHeaders: {
                    font: {
                        color: replaceColor(style.landingPage?.sectionSubHeaders?.font?.color),
                    },
                },
                bullet: {
                    color: replaceColor(style.landingPage?.bullet?.color) || style.global.secondary.color,
                },
                tabs: {
                    background: {
                        color: replaceColor(style.landingPage?.tabs?.background?.color),
                    },
                },
            },
            courseDeliveryPage: {
                courseOutlineIcon: {
                    color: replaceColor(style.courseDeliveryPage?.courseOutlineIcon?.color),
                },
                courseOutlineButton: {
                    color: replaceColor(style.courseDeliveryPage?.courseOutlineButton?.color),
                },
                lessonTitle: {
                    font: {
                        color: replaceColor(style.courseDeliveryPage?.lessonTitle?.font?.color),
                    },
                },
                notesBoxTextColor: replaceColor(style.courseDeliveryPage?.notesBoxTextColor),
                notesBoxIconColor: replaceColor(style.courseDeliveryPage?.notesBoxIconColor),
                courseProgressBarColor: replaceColor(style.courseDeliveryPage?.courseProgressBarColor),
                courseProgressIconColor: replaceColor(style.courseDeliveryPage?.courseProgressIconColor),
                courseProgressTextColor: replaceColor(style.courseDeliveryPage?.courseProgressTextColor),
            },
            menu: {
                menuSeparator: {
                    color: replaceColor(style.menu?.menuSeparator?.color),
                },
                subMenu: {
                    font: {
                        color: replaceColor(style.menu?.subMenu?.font?.color),
                    },
                },
            },
        },
        components: {
            MuiAppBar: {
                styleOverrides: {
                    root: ({ ownerState }) => ({
                        ...(ownerState.className?.includes('courseInfoBar') && {
                            backgroundColor: replaceColor(style.courseDeliveryPage?.courseInfoBar?.background?.color),
                        }),
                    }),
                },
            },
            MuiBottomNavigation: {
                styleOverrides: {
                    root: {
                        backgroundColor: '#4a4a4a', // replaceColor(style.footer?.background?.color),
                        // color: replaceColor(style.footer?.links?.color),
                    },
                },
            },
            MuiDivider: {
                styleOverrides: {
                    root: {
                        borderStyle: 'solid',
                        borderBottomWidth: '1px',
                        borderColor: replaceColor(style.site?.lineColor) || palette.dividerSecondary,
                    },
                },
            },
            StyledDivider: {
                styleOverrides: {
                    root: {
                        borderStyle: 'solid',
                        borderBottomWidth: '2px',
                        borderTopWidth: '2px',
                        borderColor: replaceColor(style.site?.lineColor) || palette.dividerSecondary,
                    },
                },
            },
            MuiTypography: {
                styleOverrides: {
                    root: ({ ownerState }) => ({
                        ...(ownerState.variant === 'subtitle1' && {
                            color: replaceColor(style.global?.secondaryFont?.color) || palette.greyCustom[800],
                        }),
                        ...(ownerState.className === 'systemPageTitle' && fontProperties(style.site?.system?.pages?.pageTitle?.font, { fontWeight: 800, fontSize: '1rem' }, replaceColor)),
                        ...(ownerState.className === 'systemPagesMessage' && fontProperties(style.site?.system.pages.message?.font, {}, replaceColor)),
                        ...(ownerState.className === 'lessonTitle' && fontProperties(style.courseDeliveryPage?.lessonTitle?.font, { fontWeight: 600, fontSize: '1rem' }, replaceColor)),
                        ...(ownerState.className === 'courseTitleSalesCard' && fontProperties(style.courseDisplay?.courseTitleSalesCard?.font, {}, replaceColor)),
                        ...(ownerState.className === 'courseTitleFeatureCard' && fontProperties(style.courseDisplay?.courseTitleFeatureCard?.font, {}, replaceColor)),
                        ...(ownerState.className === 'courseTitleLandingPage' && fontProperties(style.courseDisplay?.courseTitleLandingPage?.font, {}, replaceColor)),
                        ...(ownerState.className === 'courseHeadline' && fontProperties(style.courseDisplay?.courseHeadline?.font, {}, replaceColor)),
                        ...(ownerState.className === 'courseDisplayCourseInfoBarText' && fontProperties(style.courseDisplay?.courseInfoBarText?.font, {}, replaceColor)),
                        ...(ownerState.className === 'instructor' && fontProperties(style.courseDisplay?.instructor?.font, {}, replaceColor)),
                        ...(ownerState.className === 'description' && fontProperties(style.courseDisplay?.description?.font, {}, replaceColor)),
                        ...(ownerState.className === 'finalPrice' && fontProperties(style?.global?.font, {}, replaceColor)),
                        ...(ownerState.className === 'landingpage-section-header' && fontProperties(style.landingPage?.sectionHeaders?.font, {}, replaceColor)),
                        ...(ownerState.className === 'landingpage-section-subheader' && fontProperties(style.landingPage?.sectionSubHeaders?.font, {}, replaceColor)),
                        ...(ownerState.className === 'landingpage-section-text' && fontProperties(style.landingPage?.sectionText?.font, {}, replaceColor)),
                        ...(ownerState.className?.includes('footerListItemText') && fontProperties(style.footer?.link?.font, { fontSize: '0.75rem', color: palette.greyCustom[600] }, replaceColor)),
                        ...(ownerState.className?.includes('footerHeading') && fontProperties(style.footer?.heading?.font, { fontSize: '1rem', color: palette.greyCustom[800], fontWeight: 700 }, replaceColor)),
                        ...(ownerState.className?.includes('courseInfoBarLabel') && fontProperties(style.courseDeliveryPage?.courseBarLabels?.font, {}, replaceColor)),
                        ...(ownerState.className?.includes('courseInfoBarTitle') && fontProperties(style.courseDeliveryPage?.courseBarTitle?.font, {}, replaceColor)),
                        ...(ownerState.className?.includes('courseOutlineText') && fontProperties(style.courseDeliveryPage?.courseOutline?.font, { fontWeight: '600' }, replaceColor)),
                        ...(ownerState.className === 'showMoreLinkColor' && {
                            color: replaceColor(style.courseDisplay?.showMoreLinkColor?.color),
                        }),
                    }),
                },
            },
            MuiTab: {
                styleOverrides: {
                    root: ({ ownerState }) => ({
                        ...tabProperties(style.site?.tabs, replaceColor),
                        ...(ownerState.className?.includes('courseDeliveryTabs')
                            && tabProperties(style.courseDeliveryPage?.courseTabs, replaceColor)),
                        ...(ownerState.className?.includes('siteTabs')
                            && tabProperties(style.site?.tabs, replaceColor)),
                        ...(ownerState.className?.includes('courseInfoTabs')
                            && tabProperties(style.landingPage?.courseInfoTabs, replaceColor)),
                    }),
                },
            },
            MuiTabs: {
                defaultProps: {
                    TabIndicatorProps: { style: { display: 'none' } },
                },
            },
            MuiIconButton: {
                styleOverrides: {
                    root: ({ ownerState }) => ({
                        ...(ownerState.className?.includes('primary') && {
                            backgroundColor: replaceColor(style.site?.iconColor?.color)
                                || palette.contract.secondary,
                            color: palette.common.white,
                            '&:hover': {
                                backgroundColor: replaceColor(style.site?.iconColor?.hover?.color)
                                    || palette.contract.secondary,
                            },
                            '&.Mui-disabled': {
                                color: replaceColor(style.site?.buttons?.font?.disabled?.color),
                                backgroundColor: replaceColor(style.site?.buttons?.background?.disabled?.color),
                                borderColor: replaceColor(style.site?.buttons?.border?.disabled?.color),
                            },
                        }),
                    }),
                },
            },
            MuiLink: {
                styleOverrides: {
                    root: ({ ownerState }) => ({
                        ...(ownerState.className?.includes('pageMenu') && {
                            color: replaceColor(style.menu?.menu?.font?.color) || palette.contract.main,
                            fontWeight: style.menu?.menu?.font?.fontWeight || 700,
                            fontSize: style.menu?.menu?.font?.fontSize ? `${style.menu?.menu?.font?.fontSize}px` : '1rem',
                            textTransform: style.menu?.menu?.font?.textTransform || 'capitalize',
                            backgroundColor: replaceColor(style.menu?.menu?.backgroundColor),
                        }),
                        ...(ownerState.className?.includes('pageSubMenu') && {
                            color: replaceColor(style.menu?.subMenu?.font?.color),
                            fontWeight: style.menu?.subMenu?.font?.fontWeight,
                            fontSize: `${style.menu?.subMenu?.font?.fontSize}px`,
                            textTransform: style.menu?.subMenu?.font?.textTransform,
                            backgroundColor: replaceColor(style.menu?.subMenu?.backgroundColor),
                        }),
                        ...(ownerState.className?.includes('courseInfoTabs') && {
                            color: replaceColor(style.landingPage?.courseInfoTabs?.font?.color),
                            fontWeight: style.landingPage?.courseInfoTabs?.font?.fontWeight,
                            fontSize: `${style.landingPage?.courseInfoTabs?.font?.fontSize}`,
                            textTransform: style.landingPage?.courseInfoTabs?.font?.textTransform,
                            backgroundColor: replaceColor(style.landingPage?.courseInfoTabs?.background?.color),
                            '&:hover': {
                                color: style.landingPage?.courseInfoTabs?.font?.hover?.color,
                                backgroundColor: style.landingPage?.courseInfoTabs?.background?.hover?.color,
                            },
                        }),
                    }),
                },
            },
            MuiPaper: {
                styleOverrides: {
                    root: ({ ownerState }) => ({
                        color: replaceColor(style.global?.font?.color),
                        ...(ownerState.className?.includes('courseInfoTabs') && {
                            color: replaceColor(style.landingPage?.courseInfoTabs?.font?.color),
                            fontWeight: style.landingPage?.courseInfoTabs?.font?.fontWeight,
                            fontSize: `${style.landingPage?.courseInfoTabs?.font?.fontSize}px`,
                            textTransform: style.landingPage?.courseInfoTabs?.font?.textTransform,
                            backgroundColor: replaceColor(style.landingPage?.courseInfoTabs?.background?.color),
                            '&:hover': {
                                color: replaceColor(style.landingPage?.courseInfoTabs?.font?.hover?.color),
                                backgroundColor: replaceColor(style.landingPage?.courseInfoTabs?.background?.hover?.color),
                            },
                        }),
                    }),
                },
            },
            MuiButton: {
                styleOverrides: {
                    root: ({ ownerState }) => ({
                        // be careful because includes matches partial strings
                        // and could replace earlier settings
                        ...(ownerState.className?.includes('site-system-button-primary')
                            && buttonProperties(style.site?.system?.buttons?.primary, replaceColor)),
                        ...(ownerState.className?.includes('site-system-button-secondary')
                            && buttonProperties(style.site?.system?.buttons?.secondary, replaceColor)),
                        ...(ownerState.className?.includes('site-button')
                            && buttonProperties(style.site?.buttons, replaceColor)),
                        ...(ownerState.className?.includes('cta-button')
                            && buttonProperties(style.site?.ctaButtons, replaceColor)),
                        ...(ownerState.className?.includes('pagemenu-button') && {
                            color: replaceColor(style.menu?.menu?.font?.color) || palette.contract.main,
                            fontWeight: style.menu?.menu?.font?.fontWeight || 700,
                            fontSize: style.menu?.menu?.font?.fontSize ? `${style.menu?.menu?.font?.fontSize}px` : '1rem',
                            textTransform: style.menu?.menu?.font?.textTransform || 'capitalize',
                            backgroundColor: replaceColor(style.menu?.menu?.backgroundColor),
                        }),
                        ...(ownerState.className?.includes('ctaLinksButton')
                            && buttonProperties(style.courseDeliveryPage?.ctaLinksButton, replaceColor)),
                    }),
                },
            },
            MuiChip: {
                styleOverrides: {
                    root: ({ ownerState }) => ({
                        // be careful because includes matches partial strings
                        // and could replace earlier settings
                        ...(ownerState.className?.includes('site-system-chip-primary')
                            && chipProperties(style.site?.system?.chips?.primary, replaceColor)),
                        ...(ownerState.className?.includes('site-system-chip-secondary')
                            && chipProperties(style.site?.system?.chips?.secondary, replaceColor)),
                    }),
                },
            },
            MuiTableCell: {
                styleOverrides: {
                    root: {
                        borderColor: replaceColor(style.site?.lineColor) || palette.dividerSecondary,
                    },
                },
            },
            MuiLinearProgress: {
                styleOverrides: {
                    root: {
                        backgroundColor: replaceColor(style.site?.progressBar?.bar?.color),
                        '& .MuiLinearProgress-bar': {
                            backgroundColor: replaceColor(style.site?.progressBar?.color),
                        },
                    },
                },
            },
            MuiMenu: {
                styleOverrides: {
                    root: {
                        '& .MuiMenu-paper': {
                            backgroundColor: replaceColor(style.menu?.subMenu?.background?.color)
                                || replaceColor(style.site?.paper?.color),
                        },
                    },
                },
            },
            MuiFormControlLabel: {
                variants: [
                    {
                        props: { labelPlacement: 'top' },
                        style: {
                            '.MuiFormControlLabel-label': {
                                // show label on top left corner
                                width: '100%',
                                textAlign: 'left',
                            },
                            '.MuiStack-root': {
                                width: '100%',
                            },
                        },
                    },
                ],
                styleOverrides: {
                    root: {
                        width: '100%',
                        marginLeft: 0,
                        marginRight: 0,
                    },
                },
            },
            MuiInputBase: {
                styleOverrides: {
                    root: {
                        fontSize: '1rem',
                    },
                },
            },
        },
    };
    // console.log('finalPalette:', { finalPalette, style });
    return createTheme(finalPalette);
};
export const applyThemeColor = (stylesheet, colorCode) => (colorMapLookup(stylesheet.global, colorCode));
export default portalTheme;
//# sourceMappingURL=portalTheme.js.map